/**
 * Symptom matching for the homepage box.
 *
 * Copy lives in the locale files (`home.symptoms.*`), so this module holds
 * only the matching rules and the group ids. Fix counts come from the
 * generated catalog, never from hand-typed numbers.
 */

import catalog from '../data/catalog.json'

export type GroupId =
  | 'games' | 'network' | 'slow' | 'storage'
  | 'security' | 'updates' | 'printer' | 'audio' | 'unknown'

export interface Diagnosis {
  group: GroupId
  /** How many certified fixes exist for this group today. */
  fixCount: number
  /** Set when a Minecraft crash report was parsed. */
  crash?: { failureType: string; minecraft?: string; java?: string; memoryGb?: string }
}

const RULES: ReadonlyArray<{ group: Exclude<GroupId, 'unknown'>; pattern: RegExp }> = [
  { group: 'games', pattern: /minecraft|mod ?pack|\bmods?\b|forge|fabric|java edition|crash report|roblox|skyrim|steam|\bgame\b/i },
  { group: 'network', pattern: /wi-?fi|internet|network|dns|router|connection|offline|won'?t connect|disconnect/i },
  { group: 'slow', pattern: /slow|lag|sluggish|freez|hang|spinning|beach ?ball|takes forever|unresponsive|old computer/i },
  { group: 'storage', pattern: /disk|storage|space|full|no room|clean ?up|hard drive/i },
  { group: 'security', pattern: /virus|malware|popup|pop-?up|hijack|browser changed|search engine|adware|scam|\bads\b/i },
  { group: 'updates', pattern: /updat|upgrad|install fail|won'?t install|patch/i },
  { group: 'printer', pattern: /print|scanner|paper|spool/i },
  { group: 'audio', pattern: /sound|audio|speaker|microphone|headphone|bluetooth|volume/i },
]

const countFor = (group: GroupId): number =>
  catalog.groups.find((g) => g.id === group)?.count ?? 0

/**
 * Which problem each hero chip stands for, index-aligned with
 * `home.ask.chips` in the locale files.
 *
 * Chips select a group directly rather than passing their label through the
 * matcher below — the matcher is English-only, so a translated label would
 * never match and every chip would fall through to "unknown".
 */
export const CHIP_GROUPS: readonly GroupId[] = [
  'slow', 'network', 'games', 'storage', 'updates', 'printer',
]

export const totalFixes = catalog.total
export const vendorFixes = catalog.vendor
export const catalogGeneratedAt = catalog.generatedAt

export const groupCount = countFor

export const diagnose = (text: string): Diagnosis => {
  const match = RULES.find((r) => r.pattern.test(text))
  const group: GroupId = match?.group ?? 'unknown'
  return { group, fixCount: countFor(group) }
}

/* ---------- Minecraft crash reports ---------- */

/** Mirrors the failure classes in crates/justfixit-minecraft-core/src/crash_parser.rs */
export type FailureType =
  | 'out_of_memory' | 'missing_dependency' | 'mod_conflict'
  | 'java_mismatch' | 'missing_class' | 'unrecognized' | 'not_a_crash_report'

export interface CrashResult extends Diagnosis {
  failureType: FailureType
  /** Name pulled out of the report, e.g. the add-on that is missing. */
  subject?: string
}

const gb = (bytes?: string) =>
  bytes ? `${Math.round((Number(bytes) / 1073741824) * 10) / 10} GB` : undefined

export const readCrashReport = (text: string): CrashResult => {
  const base = { group: 'games' as const, fixCount: countFor('games') }

  if (!/Minecraft Crash Report/i.test(text)) {
    return { ...base, failureType: 'not_a_crash_report' }
  }

  const crash = {
    failureType: '',
    minecraft: /Minecraft Version:\s*(\S+)/.exec(text)?.[1],
    java: /Java Version:\s*([^\n,]+)/.exec(text)?.[1]?.trim(),
    memoryGb: gb(/up to (\d+) bytes/.exec(text)?.[1]),
  }

  const done = (failureType: FailureType, subject?: string): CrashResult => ({
    ...base,
    failureType,
    subject,
    crash: { ...crash, failureType },
  })

  if (/OutOfMemoryError|GC overhead limit exceeded/.test(text)) return done('out_of_memory')
  if (/ModLoadingException|Missing or unsupported mandatory dependencies|Incompatible mod set/.test(text)) {
    return done('missing_dependency', /requires '([^']+)'/.exec(text)?.[1])
  }
  if (/MixinApplyError|org\.spongepowered\.asm\.mixin/.test(text)) return done('mod_conflict')
  if (/UnsupportedClassVersionError|class file version/.test(text)) return done('java_mismatch')
  if (/NoClassDefFoundError|ClassNotFoundException/.test(text)) return done('missing_class')
  return done('unrecognized')
}
